import PropTypes from "prop-types";
import { memo, useContext, useMemo } from "react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import SwiperCore, { Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import StoreContext from "../../context/store";
import { useTracking } from "../../context/Tracking";
import useDeviceDetect from "../../hooks/useDeviceDetect";
import useResponsiveWithHydrationFix from "../../hooks/useResponsiveWithHydrationFix";
import ProductRecommendationsButton from "./productRecommendations/ProductRecommendationsButton";
import { ComponentType } from "../../constants/ComponentType";

import "swiper/swiper.min.css";
import "swiper/swiper-bundle.min.css";
import "./productRecommendations/productRecommendations.scss";

SwiperCore.use([Navigation]);

function ProductRecommendations({ content = {}, componentType }) {
  const { setRecommendedProductsPopupData } = useContext(StoreContext);
  const { title = "", buttonText = "View", recommendations = [] } = content;
  const { isMobile } = useDeviceDetect();
  const { trackRecommendedProductsPopupAction } = useTracking();

  const slidesView = componentType === ComponentType.PDP ? 1.7 : 4;
  const slidesGroup = componentType === ComponentType.PDP ? 1 : 4;
  const spaceBetweenSlides = componentType === ComponentType.PDP ? 20 : 8;

  const onButtonClick = (handles) => {
    trackRecommendedProductsPopupAction({
      type: "opened",
    });

    setRecommendedProductsPopupData({ open: true, title, handles });
  };

  const imagesToRender = useMemo(
    () =>
      recommendations.reduce((items, item) => {
        const { image = {}, handles = [] } = item;
        const { altText = "", desktopImage, mobileImage } = image;

        const key = mobileImage?.url || desktopImage?.url || altText;

        // Mobile version
        if (isMobile && mobileImage?.gatsbyImageData && !mobileImage?.url?.includes("empty.png")) {
          items.push(
            <div className="recommendations-images-wrapper" key={key}>
              <div className="recommendations-image" key={key}>
                <GatsbyImage
                  className={`recommendations-images__mobile__image ${
                    componentType === ComponentType.PDP
                      ? "recommendations-images__mobile__image--pdp"
                      : ""
                  }`}
                  alt={altText}
                  image={getImage(mobileImage.gatsbyImageData)}
                />
              </div>
              <ProductRecommendationsButton
                buttonText={buttonText}
                onClick={() => onButtonClick(handles)}
              />
            </div>
          );
        }

        // Desktop version
        if (
          !isMobile &&
          desktopImage?.gatsbyImageData &&
          !desktopImage?.url?.includes("empty.png")
        ) {
          items.push(
            <SwiperSlide key={key}>
              <div className="recommendations-image">
                <GatsbyImage
                  className="recommendations-images__desktop__image"
                  alt={altText}
                  image={getImage(desktopImage.gatsbyImageData)}
                />
              </div>
              <ProductRecommendationsButton
                buttonText={buttonText}
                onClick={() => onButtonClick(handles)}
              />
            </SwiperSlide>
          );
        }

        return items;
      }, []),
    [recommendations, buttonText, isMobile, trackRecommendedProductsPopupAction, componentType]
  );

  const MobileLayout = <div className="recommendations-images__mobile">{imagesToRender}</div>;

  const DesktopLayout = (
    <Swiper
      navigation={{
        nextEl: ".scrollable-next-button",
        prevEl: ".scrollable-prev-button",
      }}
      slidesPerView={slidesView}
      slidesPerGroup={slidesGroup}
      spaceBetween={spaceBetweenSlides}
    >
      {imagesToRender}
    </Swiper>
  );

  const imageLayout = useResponsiveWithHydrationFix(MobileLayout, DesktopLayout);

  return (
    <div
      className={`recommendations-images__block ${
        componentType === ComponentType.PDP && isMobile ? "recommendations-images__block--pdp" : ""
      }`}
    >
      <h2
        className={
          componentType === ComponentType.PDP
            ? "recommendations-images__block__title__pdp"
            : "recommendations-images__block__title"
        }
      >
        {title}
      </h2>
      {imageLayout}
    </div>
  );
}

export default memo(ProductRecommendations);

ProductRecommendations.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    buttonText: PropTypes.string,
    recommendations: PropTypes.arrayOf(
      PropTypes.shape({
        handles: PropTypes.string.isRequired,
        image: PropTypes.shape({
          altText: PropTypes.string,
          desktopImage: PropTypes.shape({
            url: PropTypes.string,
            gatsbyImageData: PropTypes.shape({}).isRequired,
          }),
          mobileImage: PropTypes.shape({
            url: PropTypes.string,
            gatsbyImageData: PropTypes.shape({}).isRequired,
          }),
        }).isRequired,
      })
    ),
  }).isRequired,
  componentType: PropTypes.oneOf(Object.values(ComponentType)).isRequired,
};
